import React, { useEffect, useRef, useState } from "react";

// Inline audio player with a faux-waveform visualization (cheap visual,
// no real FFT). Shows progress and lets the user scrub.
const AudioPlayer = ({ src, primaryColor = "#3E8DFF", textColor = "#0f172a", barColor = "#94a3b8" }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime = () => {
      if (audio.duration > 0) setProgress((audio.currentTime / audio.duration) * 100);
    };
    const onLoaded = () => setDuration(audio.duration || 0);
    const onEnded = () => setPlaying(false);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onLoaded);
    audio.addEventListener("ended", onEnded);
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onLoaded);
      audio.removeEventListener("ended", onEnded);
    };
  }, []);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      audio.play();
      setPlaying(true);
    }
  };

  const seek = (e) => {
    const audio = audioRef.current;
    if (!audio || !audio.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    audio.currentTime = pct * audio.duration;
  };

  // Generate a stable pseudo-random bar pattern from src string so each
  // proof's "waveform" looks different but consistent across renders.
  const bars = React.useMemo(() => {
    const seed = Array.from(String(src || "")).reduce((a, c) => a + c.charCodeAt(0), 0);
    const arr = [];
    let x = seed;
    for (let i = 0; i < 48; i++) {
      x = (x * 9301 + 49297) % 233280;
      const v = (x / 233280);
      arr.push(0.25 + v * 0.75);
    }
    return arr;
  }, [src]);

  const formatTime = (s) => {
    if (!s || isNaN(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  return (
    <div className="flex items-center gap-3 w-full">
      <audio ref={audioRef} src={src} preload="metadata" />
      <button
        type="button"
        onClick={toggle}
        className="flex-none w-10 h-10 rounded-full flex items-center justify-center text-white transition-transform hover:scale-105"
        style={{ background: primaryColor }}
        aria-label={playing ? "Pause" : "Play"}
      >
        {playing ? (
          <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
            <rect x="6" y="5" width="4" height="14" rx="1" />
            <rect x="14" y="5" width="4" height="14" rx="1" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 ml-0.5">
            <path d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z" />
          </svg>
        )}
      </button>

      <div className="flex-1 min-w-0">
        <div
          className="h-10 flex items-center gap-[2px] cursor-pointer"
          onClick={seek}
          role="slider"
          aria-valuenow={Math.round(progress)}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          {bars.map((h, i) => {
            const reached = (i / bars.length) * 100 < progress;
            return (
              <span
                key={i}
                className="flex-1 rounded-full transition-colors"
                style={{
                  height: `${Math.round(h * 100)}%`,
                  background: reached ? primaryColor : barColor,
                  opacity: reached ? 1 : 0.5,
                }}
              />
            );
          })}
        </div>
        <div className="flex justify-between text-[10px] font-bold mt-0.5" style={{ color: textColor, opacity: 0.7 }}>
          <span>{formatTime((progress / 100) * duration)}</span>
          <span>{formatTime(duration)}</span>
        </div>
      </div>
    </div>
  );
};

export default AudioPlayer;
